import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";

const emptyForm = { name: "", active: true };

/** Bank master: the list behind the Bank dropdown on the payment screens and in the mobile app. */
const Banks = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadBanks = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/banks");
      setBanks(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load banks", error);
      toast.error(error.message || "Failed to load banks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBanks();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (bank) => {
    setEditingId(bank.id);
    setFormData({ name: bank.name || "", active: bank.active ?? true });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: formData.name.trim(), active: formData.active };
      if (editingId) {
        await api.put(`/api/v1/banks/${editingId}`, payload);
        toast.success("Bank updated");
      } else {
        await api.post("/api/v1/banks", payload);
        toast.success("Bank added");
      }
      setShowModal(false);
      loadBanks();
    } catch (error) {
      console.error("Failed to save bank", error);
      toast.error(error.message || "Failed to save bank");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading banks...</div>
          </div>
        </div>
      ) : null}

      <div className="filters-bar">
        <button className="primary" onClick={openCreate}>
          + New Bank
        </button>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Bank</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {banks.length === 0 ? (
              <tr>
                <td colSpan={4}>No banks found.</td>
              </tr>
            ) : (
              banks.map((bank) => (
                <tr key={bank.id}>
                  <td>{bank.id}</td>
                  <td>{bank.name}</td>
                  <td>
                    <span className={`badge ${bank.active ? "badge-green" : "badge-gray"}`}>{bank.active ? "Active" : "Inactive"}</span>
                  </td>
                  <td className="action-buttons">
                    <button className="secondary" onClick={() => openEdit(bank)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">{editingId ? "Edit Bank" : "Add Bank"}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Bank Name</label>
                  <input value={formData.name} onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))} required maxLength={150} />
                </div>
                <div className="form-group">
                  <label>Status (inactive banks are hidden from the dropdowns)</label>
                  <select
                    value={formData.active ? "true" : "false"}
                    onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.value === "true" }))}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Banks;
